/**
 * Le **sous-ensemble Markdown** que les énoncés emploient réellement.
 *
 * Jumeau TypeScript de `src/teacher/shared/enonce_markdown.py`, et vérifié sur
 * le **même corpus de cas** — deux implémentations sans corpus commun divergent,
 * et la divergence ne se voit qu'à l'écran.
 *
 * Les contextes de génération écrivent du Markdown sans qu'on le leur ait
 * demandé, et rien ne le rendait. Mesuré le 20 août sur 2 170 énoncés servis :
 * **642** portent une liste numérotée, 80 du gras, 63 une liste à tirets, 20 un
 * tableau. L'enfant lisait donc `| Ce qui a été mesuré | Avant le barrage |`
 * suivi d'une ligne de tirets — sur l'exercice où le tableau *est* la donnée.
 *
 * **Le sous-ensemble est délibérément petit**, et fermé : paragraphes, gras,
 * listes, tableaux. Ni titres, ni liens, ni images — un lien dans un énoncé
 * scolaire est une sortie hors de l'exercice, et une image y entrerait sans
 * passer par les portes du §19 bis. Ce qui n'est pas dans la liste est laissé
 * tel quel, jamais interprété.
 *
 * **Les formules ne sont pas touchées** : un fragment `\(…\)` est mis de côté
 * avant l'analyse et remis après. Sans cela la valeur absolue `\(|x|\)` serait
 * lue comme deux cellules de tableau.
 */

/** Les fragments mathématiques, mis de côté avant l'analyse Markdown. */
const FORMULE = /\\\(.+?\\\)/gs;
const GRAS = /\*\*(.+?)\*\*/g;
const LIGNE_TABLEAU = /^\s*\|(.+)\|\s*$/;
const SEPARATEUR = /^\s*\|[\s:|-]+\|\s*$/;
const PUCE = /^\s*[-*]\s+(.*)$/;
const NUMERO = /^\s*\d+\.\s+(.*)$/;
/** Quatre espaces d'indentation : la convention du Markdown d'origine, et celle
 *  qu'emploient déjà 202 des énoncés servis. */
const INDENTE = /^ {4}(.*)$/;
/** Une clôture explicite, pour un bloc dont la première ligne n'est pas indentée. */
const BARRIERE = /^\s*```/;

/**
 * La sentinelle qui tient la place d'une formule pendant l'analyse Markdown.
 *
 * U+0000 ne peut pas apparaître dans un énoncé, et aucune des expressions
 * ci-dessus ne le reconnaît. Une sentinelle faite de caractères ordinaires —
 * « @@0@@ » par exemple — serait, elle, transformable par les règles qu'elle est
 * précisément censée traverser intacte.
 */
const MARQUE = "\u0000";

function echapper(texte: string): string {
  return texte
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Rendre le Markdown en HTML, **les formules laissées en place sous forme de
 * sentinelles**. L'appelant les remplace par le rendu KaTeX.
 *
 * Le partage des responsabilités n'est pas cosmétique : ce module ne sait pas
 * rendre une formule et ne doit pas apprendre à le faire — `TexteMathematique`
 * porte déjà cette règle, avec son propre traitement d'une formule illisible.
 */
export function rendreMarkdown(source: string): { html: string; formules: string[] } {
  const formules: string[] = [];
  const protege = (source || "").replace(FORMULE, (trouve) => {
    formules.push(trouve);
    return `${MARQUE}${formules.length - 1}${MARQUE}`;
  });

  const enligne = (fragment: string): string =>
    echapper(fragment).replace(GRAS, "<strong>$1</strong>");

  const cellules = (ligne: string): string[] => {
    const dedans = LIGNE_TABLEAU.exec(ligne);
    return (dedans ? (dedans[1] ?? "") : ligne).split("|").map((c) => c.trim());
  };

  const lignes = protege.split("\n");
  const sortie: string[] = [];
  let tampon: string[] = [];
  const vider = () => {
    if (tampon.length) {
      // **Les lignes ne sont pas jointes.** Markdown les recolle parce qu'il
      // sert de la prose ; un énoncé, non — un retour à la ligne y sépare
      // presque toujours quelque chose. 2 029 des 2 201 énoncés servis en
      // contiennent au moins un.
      sortie.push(`<p>${tampon.map(enligne).join("<br>")}</p>`);
      tampon = [];
    }
  };

  /** Un bloc dont les espaces et les retours à la ligne **sont** le contenu.
   *
   *  Le défaut le plus coûteux qu'ait produit ce module. Un exercice d'anglais
   *  demandait d'identifier quatre documents *d'après leur mise en page* ; le
   *  rendu joignait les lignes et le HTML écrasait les espaces, si bien que
   *  l'enfant à qui l'on demandait de lire une mise en page n'en avait plus
   *  aucune. L'exercice restait juste, vérifié, bien noté — et insoluble.
   *
   *  Rien n'est interprété là-dedans : un `|` de cadre n'est pas une cellule, et
   *  deux astérisques de dessin ne sont pas de l'emphase. */
  const preformate = (bloc: string[]) => {
    sortie.push(`<pre>${echapper(bloc.join("\n"))}</pre>`);
  };

  let i = 0;
  while (i < lignes.length) {
    const ligne = lignes[i] ?? "";
    if (!ligne.trim()) {
      vider();
      i += 1;
      continue;
    }

    // Le bloc préformaté d'abord : ce qu'il contient ne doit être reconnu par
    // aucune autre règle.
    if (BARRIERE.test(ligne)) {
      vider();
      i += 1;
      const bloc: string[] = [];
      while (i < lignes.length && !BARRIERE.test(lignes[i] ?? "")) {
        bloc.push(lignes[i] ?? "");
        i += 1;
      }
      i += 1; // la barrière de fermeture, jamais affichée
      preformate(bloc);
      continue;
    }

    if (INDENTE.test(ligne)) {
      vider();
      const bloc: string[] = [];
      // Une ligne vide **à l'intérieur** d'un bloc en fait partie : une carte
      // postale sépare son en-tête de son corps par une ligne vide, et couper
      // là en ferait deux blocs sans rapport.
      while (i < lignes.length) {
        const trouve = INDENTE.exec(lignes[i] ?? "");
        if (trouve) {
          bloc.push(trouve[1] ?? "");
          i += 1;
        } else if (
          !(lignes[i] ?? "").trim() &&
          lignes.slice(i + 1, i + 3).some((suivante) => INDENTE.test(suivante))
        ) {
          bloc.push("");
          i += 1;
        } else {
          break;
        }
      }
      preformate(bloc);
      continue;
    }

    // Un tableau se reconnaît à sa **ligne de séparation**, pas à sa première
    // barre : une phrase contenant une barre verticale n'en est pas un, et la
    // prendre pour tel transformerait un énoncé en tableau d'une seule cellule.
    if (LIGNE_TABLEAU.test(ligne) && SEPARATEUR.test(lignes[i + 1] ?? "")) {
      vider();
      const entete = cellules(ligne);
      i += 2; // l'en-tête et le séparateur, qui n'est jamais affiché
      const corps: string[][] = [];
      while (i < lignes.length && LIGNE_TABLEAU.test(lignes[i] ?? "")) {
        corps.push(cellules(lignes[i] ?? ""));
        i += 1;
      }
      const th = entete.map((c) => `<th>${enligne(c)}</th>`).join("");
      const trs = corps
        .map((r) => `<tr>${r.map((c) => `<td>${enligne(c)}</td>`).join("")}</tr>`)
        .join("");
      sortie.push(`<table><thead><tr>${th}</tr></thead><tbody>${trs}</tbody></table>`);
      continue;
    }

    let liste = false;
    for (const [motif, balise] of [
      [PUCE, "ul"],
      [NUMERO, "ol"],
    ] as const) {
      if (!motif.test(ligne)) continue;
      vider();
      const items: string[] = [];
      let trouve = motif.exec(lignes[i] ?? "");
      while (i < lignes.length && trouve) {
        items.push(`<li>${enligne(trouve[1] ?? "")}</li>`);
        i += 1;
        trouve = i < lignes.length ? motif.exec(lignes[i] ?? "") : null;
      }
      sortie.push(`<${balise}>${items.join("")}</${balise}>`);
      liste = true;
      break;
    }
    if (liste) continue;

    tampon.push(ligne.trim());
    i += 1;
  }
  vider();
  return { html: sortie.join(""), formules };
}

/** Découper le HTML rendu autour de ses sentinelles, pour que l'appelant place
 *  les formules sans jamais concaténer du HTML à la main. */
export function separerFormules(html: string): Array<{ html: string } | { indice: number }> {
  const morceaux: Array<{ html: string } | { indice: number }> = [];
  const motif = new RegExp(`${MARQUE}(\\d+)${MARQUE}`, "g");
  let curseur = 0;
  for (const trouve of html.matchAll(motif)) {
    const debut = trouve.index ?? 0;
    if (debut > curseur) morceaux.push({ html: html.slice(curseur, debut) });
    morceaux.push({ indice: Number(trouve[1]) });
    curseur = debut + trouve[0].length;
  }
  if (curseur < html.length) morceaux.push({ html: html.slice(curseur) });
  return morceaux;
}
